import { Button, HistoryTransaction, Modal, Steps } from '@hub/shared'
import React, { useCallback, useLayoutEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import './modal-antecipation-onboarding.component.scoped.scss'

interface Props extends React.HTMLAttributes<HTMLDivElement> {
  openModal: boolean
  transactionObject: HistoryTransaction | null
  closeModal?: () => any
}

enum StepsEnum {
  whatIs = 0,
  discount = 1,
  example = 2,
  postAccepted = 3,
}

export default ({ openModal, transactionObject, closeModal }: Props) => {
  const [t] = useTranslation('modalAntecipationOnboarding')

  const [width, setWidth] = useState(window?.innerWidth)
  const [height, setHeight] = useState(window?.innerHeight)
  const [activeStep, setActiveStep] = useState<StepsEnum>(0)

  const formatValue = (value?: number) => {
    return value?.toLocaleString('pt-br', {
      style: 'currency',
      currency: 'BRL',
      maximumFractionDigits: 2,
    })
  }

  const steps = useMemo(
    () => [
      {
        status: t('whatIs.status'),
        icon: 'fa-calendar-day',
        text: t('whatIs.text'),
      },
      {
        status: t('discount.status'),
        icon: 'fa-sack-dollar',
        text: t('discount.text', {
          anticipationAliquot: transactionObject?.anticipation?.aliquot.toLocaleString('en', {
            style: 'percent',
            minimumFractionDigits: 1,
          }),
          grossValue: formatValue(transactionObject?.grossValue),
          anticipationValue: transactionObject?.grossValue
            ? formatValue(transactionObject?.grossValue - transactionObject?.grossValue * transactionObject?.anticipation?.aliquot)
            : null,
        }),
      },
      {
        status: t('example.status'),
        icon: 'fa-money-bill-wave',
        text: t('example.text', {
          grossValue: formatValue(transactionObject?.grossValue),
          anticipationValue: transactionObject?.grossValue
            ? formatValue(transactionObject?.grossValue - transactionObject?.grossValue * transactionObject?.anticipation?.aliquot)
            : null,
        }),
      },
      {
        status: t('postAccepted.status'),
        icon: 'fa-cfile-invoice-dollar',
        text: t('postAccepted.text'),
      },
    ],
    [transactionObject, t]
  )

  useLayoutEffect(() => {
    const updateWidth = () => {
      setWidth(window?.innerWidth)
    }
    const updateHeight = () => {
      setHeight(window?.innerHeight)
    }
    window.addEventListener('resize', updateWidth)
    window.addEventListener('resize', updateHeight)
    return () => {
      window.removeEventListener('resize', updateWidth)
      window.removeEventListener('resize', updateHeight)
    }
  }, [])

  const close = useCallback(() => {
    if (closeModal && typeof closeModal === 'function') {
      setActiveStep(0)
      closeModal()
    }
  }, [closeModal])

  return (
    <Modal
      className="modal-antecipation-onboarding"
      open={openModal}
      modalSize="lg"
      forceMobileNoMargin
      onCloseChange={() => close()}
      header={<h5 className="m-0">{t('title')}</h5>}
      footer={
        <div className="menu-modal col-24 display-flex justify-content-space-between">
          <Button
            id="cancel-button-modal-payment-onboarding"
            onClick={() => (activeStep === StepsEnum['whatIs'] ? close() : setActiveStep(activeStep - 1))}
            color="transparent"
            textColor="var(--gray)"
            borderColor="var(--gray)"
          >
            <span>{activeStep === StepsEnum['whatIs'] ? t('skip') : t('back')}</span>
          </Button>
          <Button
            id="confirm-button-modal-payment-onboarding"
            onClick={() => (activeStep === StepsEnum['postAccepted'] ? close() : setActiveStep(activeStep + 1))}
          >
            <span>{t('next')}</span>
          </Button>
        </div>
      }
    >
      <div>
        <Steps className="m-0" steps={steps?.map((step) => ({ status: step.status }))} active={activeStep} />
        <div className="content" style={width < 992 ? { height: `calc(${height}px - 165px)` } : { minHeight: '60vh' }}>
          <i className={`fa-regular ${steps[activeStep].icon}`} style={{ fontSize: '8.5rem', color: 'var(--title_color)' }}></i>
          <p dangerouslySetInnerHTML={{ __html: steps[activeStep].text }} />
        </div>
      </div>
    </Modal>
  )
}
